import type {
  AppendMessage,
  ExternalThreadQueueAdapter,
} from '@assistant-ui/core';
import {useShallow} from 'zustand/react/shallow';

import {getVisiblePromptParts} from '../messageConversions';
import type {PromptContextParams} from '../pageContext';
import {
  builtPromptFromStoreContent,
  builtPromptFromText,
  extractText,
  readBuiltPrompt,
  withPromptContext,
} from '../promptAssembly';
import {
  readAgentConfigOverride,
  readPermissions,
  shouldBypassQueue,
} from '../runConfig';
import {
  type ContentPart,
  hasMeaningfulContent,
  type Thread,
} from '../store/thread';
import {isAgentChatReadonly} from '../useAgentChatReadonly';
import {type QueuedMessage, queueKey, useQueueStore} from './store';

/**
 * useQueueAdapter builds the AUI `ExternalThreadQueueAdapter` for
 * `activeThread`, reading and writing that thread's queue. It falls back to
 * `onNew` when a message shouldn't queue (an idle thread with an empty queue,
 * an explicit bypass, or no thread yet).
 */
export function useQueueAdapter(
  activeThread: Thread | undefined,
  params: PromptContextParams,
  onNew: (message: AppendMessage) => Promise<void> | void
): ExternalThreadQueueAdapter {
  // Key by the thread's own id (optimistic before promotion, server-assigned
  // after). No active thread means the first send hasn't happened.
  const key = activeThread ? queueKey(activeThread.id) : undefined;
  const activeQueue =
    useQueueStore(useShallow(s => (key != null ? s.queues[key] : undefined))) ??
    [];
  const isRunning = activeThread?.status === 'running';

  return {
    items: activeQueue
      // A dispatched item is already in the transcript.
      .filter(m => !m.draining)
      .map(m => ({
        id: m.id,
        prompt: getQueueItemPrompt(m.content),
      })),
    enqueue: (message, _options) => {
      if (isAgentChatReadonly()) {
        return;
      }
      if (shouldBypassQueue(message)) {
        void onNew(message);
        return;
      }

      // No thread yet, so the first send creates one; nothing to queue against.
      if (key == null) {
        void onNew(message);
        return;
      }

      const queueState = useQueueStore.getState();
      const queue = queueState.queues[key] ?? [];
      if (!isRunning && queue.length === 0) {
        void onNew(message);
        return;
      }

      const queuedMessage = buildQueuedMessage(message, params);
      if (!queuedMessage) {
        return;
      }

      queueState.enqueue(key, queuedMessage);
      if (!isRunning) {
        // A live send onto an idle, paused queue is an explicit user resume.
        queueState.requestResume(key);
      }
    },
    steer: () => {
      // Steer isn't implemented yet.
    },
    remove: id => {
      if (key != null) {
        useQueueStore.getState().remove(key, id);
      }
    },
    clear: reason => {
      // AUI calls clear for several reasons, but only an edit should drop
      // queued follow-ups; cancel-run (stop) and reload keep them.
      if (reason === 'edit' && key != null) {
        useQueueStore.getState().clear(key);
      }
    },
  };
}

/**
 * buildQueuedMessage converts an AUI `AppendMessage` into a `QueuedMessage`,
 * baking in prompt context now (rather than at drain time) so a queued item's
 * content is already the same complete, ready-to-send form `onNew` would have
 * produced. Returns null for a message with no meaningful content, mirroring
 * `onNew`'s own no-op.
 */
function buildQueuedMessage(
  message: AppendMessage,
  params: PromptContextParams
): QueuedMessage | null {
  const userPrompt =
    readBuiltPrompt(message) ?? builtPromptFromText(extractText(message));
  if (!hasMeaningfulContent(userPrompt.storeParts)) {
    return null;
  }

  return {
    id: crypto.randomUUID(),
    content: withPromptContext(userPrompt, params).storeParts,
    enqueuedAt: Date.now(),
    runConfig: {
      custom: {
        agentConfigOverride: readAgentConfigOverride(message) ?? null,
        permissions: readPermissions(message),
      },
    },
  };
}

/**
 * getQueueItemPrompt derives a queue item's preview, dropping the ambient
 * context {@link withPromptContext} prepends. A run-only prompt has no text for
 * that slice to keep, so it falls back to rendering its run mentions.
 */
function getQueueItemPrompt(content: ContentPart[]): string {
  const preview = builtPromptFromStoreContent(
    getVisiblePromptParts(content)
  ).plainText;
  if (preview !== '') {
    return preview;
  }

  const runParts = content.filter(part => part.type === 'wandb-run');
  return builtPromptFromStoreContent(runParts).plainText;
}
